import React from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell
} from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import ActivityTableRow from './ActivityTableRow';

function ActivityTable({
  activities,
  isLoading,
  errorMessage,
  selectedIds,
  onToggleRow,
  onToggleAll,
  onView
}) {
  const allSelected =
    activities.length > 0 && activities.every((activity) => selectedIds.has(activity.id));

  return (
    <Table disableWrapperOverflow className="w-full min-w-[860px]">
      <TableHeader className="sticky top-0 z-10 bg-white">
        <TableRow className="border-b border-gray-200 hover:bg-transparent">
          <TableHead className="w-10 py-3 pl-2 pr-3 text-left">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onToggleAll}
              className="rounded border-gray-300 text-primary focus:ring-primary/30"
              aria-label="Select all activities"
            />
          </TableHead>
          <TableHead className="w-28 py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            Status
          </TableHead>
          <TableHead className="py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            Name
          </TableHead>
          <TableHead className="w-44 py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            Start Date
          </TableHead>
          <TableHead className="w-44 py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            Due Date
          </TableHead>
          <TableHead className="w-40 py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            Score
          </TableHead>
          <TableHead className="w-44 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            Actions
          </TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {isLoading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <TableRow key={index} className="border-b border-gray-100">
              <TableCell className="py-4 pl-2 pr-3">
                <Skeleton className="h-4 w-4 rounded" />
              </TableCell>
              <TableCell className="py-4 pr-4">
                <Skeleton className="h-4 w-20" />
              </TableCell>
              <TableCell className="py-4 pr-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              </TableCell>
              <TableCell className="py-4 pr-4">
                <Skeleton className="mb-1 h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </TableCell>
              <TableCell className="py-4 pr-4">
                <Skeleton className="mb-1 h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </TableCell>
              <TableCell className="py-4 pr-4">
                <Skeleton className="h-6 w-12 rounded-md" />
              </TableCell>
              <TableCell className="py-4">
                <Skeleton className="h-8 w-32 rounded-lg" />
              </TableCell>
            </TableRow>
          ))
        ) : errorMessage ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={7} className="py-16 text-center">
              <div className="flex flex-col items-center gap-2">
                <AlertCircle className="h-8 w-8 text-gray-300" />
                <p className="text-sm text-gray-400">{errorMessage}</p>
              </div>
            </TableCell>
          </TableRow>
        ) : activities.length === 0 ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={7} className="py-16 text-center">
              <div className="flex flex-col items-center gap-2">
                <AlertCircle className="h-8 w-8 text-gray-300" />
                <p className="text-sm text-gray-400">No activities found</p>
              </div>
            </TableCell>
          </TableRow>
        ) : (
          activities.map((activity) => (
            <ActivityTableRow
              key={activity.id}
              activity={activity}
              isSelected={selectedIds.has(activity.id)}
              onToggle={onToggleRow}
              onView={onView}
            />
          ))
        )}
      </TableBody>
    </Table>
  );
}

export default ActivityTable;
